import { useCallback } from "react";
import { ApiResponse, RequestOptions } from "@/shared/types/request";
import { useAuth } from "@workos-inc/authkit-react";
import { logger } from "./logger";

// Prevents race conditions when multiple API calls request tokens simultaneously.
// When 5 requests fire at once, they all wait for the SAME token fetch instead of
// each triggering their own refresh attempt (which causes "No access token available").
let pendingTokenRequest: Promise<string> | null = null;

function buildQueryString(
  params: Record<string, string | number | boolean>
): string {
  const queryString = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    queryString.append(key, String(params[key]));
  });
  return queryString.toString();
}

export const useApi = () => {
  const { getAccessToken, signIn } = useAuth();

  const makeApiRequest = useCallback(async ({
    endpoint,
    method,
    body,
    headers: requestHeaders = {},
    queryParams,
    otherOptions = {},
    authorization = true,
  }: RequestOptions): Promise<ApiResponse> => {
    if (method === "GET" && queryParams) {
      const queryString = buildQueryString(queryParams);
      endpoint = `${endpoint}?${queryString}`;
    }
    
    const subdomain = window.location.hostname.split('.')[0];
    let API_URL_BASE = `https://${subdomain}api.hourglass.law`;

    if (window.location.hostname.includes("bpmlaw")) {
      API_URL_BASE = `https://hourglassapi.bpmlaw.com`;
    } else if (window.location.hostname.includes("tysonmendes")) {
      API_URL_BASE = `https://hourglassapi.tysonmendes.com`;
    } else if (window.location.hostname.includes("localhost")) {
        API_URL_BASE = `http://localhost:8000`;
    } else if (window.location.hostname.includes("pregodonnell")) {
        API_URL_BASE = `https://hourglassapi.pregodonnell.com`;
    } else if (window.location.hostname.includes("app")) {
      API_URL_BASE = `https://api.hourglass.law`;
    }
    const url = `${API_URL_BASE}${endpoint}`;

    const executeFetch = async (accessToken: string): Promise<Response> => {
      const headers: HeadersInit = new Headers({
        "Content-Type": "application/json",
        ...requestHeaders,
      });

      if (authorization && accessToken) {
        headers.set("Authorization", `Bearer ${accessToken}`);
      }

      const options: RequestInit = {
        method,
        headers,
        ...otherOptions,
      };

      if (method === "POST" && body) {
        options.body = JSON.stringify(body);
      }

      return await fetch(url, options);
    };

    // Serialize token requests - if a fetch is in progress, wait for it
    // instead of triggering a second refresh attempt
    let accessToken = "";
    try {
      if (pendingTokenRequest) {
        // Another request is already fetching the token - wait for it
        accessToken = await pendingTokenRequest;
      } else {
        // We're the first request - fetch and let others wait on us
        pendingTokenRequest = getAccessToken();
        try {
          accessToken = await pendingTokenRequest;
        } finally {
          pendingTokenRequest = null;
        }
      }
    } catch (error) {
      logger.error("Failed to get access token", {
        error: error instanceof Error ? error.message : String(error)
      });
      await signIn();
      throw new Error("Authentication required - redirecting to sign in");
    }

    let response = await executeFetch(accessToken);

    // Try refreshing the token and retry the request once
    if (response.status === 401) {
      logger.warn("Got 401 response - attempting token refresh", { endpoint });

      try {
        accessToken = await getAccessToken();
        response = await executeFetch(accessToken);
      } catch (refreshError) {
        logger.error("Token refresh failed", {
          error: refreshError instanceof Error ? refreshError.message : String(refreshError),
          endpoint,
        });

        // If refresh fails, we truly need to re-authenticate
        await signIn();
        throw new Error("Token refresh failed - redirecting to sign in");
      }
    }

    // Parse the response
    const responseData = await response.json();
    const { data, type, error } = responseData;

    // Handle error responses (backend sends type: "ERROR")
    if (type === "ERROR" || !response.ok) {
      // error can be { code, message } object or a string
      const errorMessage = typeof error === 'object' && error?.message
        ? error.message
        : (error || `Request failed with status ${response.status}`);
      return { error: errorMessage, status: "ERROR" };
    }

    return { data, status: type || "SUCCESS" };
  }, [getAccessToken, signIn]);

  return { makeApiRequest };
};
